import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiRequest, apiUrl } from "../api";
import type { PlanDay, WorkoutSession } from "../types";
import { uuid } from "../utils";

interface UseWorkoutSyncOptions {
  token: string;
  onUnauthorized: () => void;
  onSessionsChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}

interface ConflictDetail {
  active_session?: WorkoutSession;
}

export function useWorkoutSync({
  token,
  onUnauthorized,
  onSessionsChanged,
  onError,
}: UseWorkoutSyncOptions) {
  const [active, setActive] = useState<WorkoutSession | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [connected, setConnected] = useState(false);
  const [selectedRpe, setSelectedRpe] = useState<number | null>(null);

  const activeRef = useRef<WorkoutSession | null>(null);
  const anchorElapsedRef = useRef(0);
  const anchorTimeRef = useRef(Date.now());
  const progressInFlightRef = useRef(false);
  const lastProgressPushRef = useRef(0);
  const streamAbortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);

  const currentElapsed = useCallback(() => {
    const session = activeRef.current;
    if (!session) return 0;
    const live = session.status === "running"
      ? Math.floor((Date.now() - anchorTimeRef.current) / 1000)
      : 0;
    return Math.max(0, anchorElapsedRef.current + live);
  }, []);

  const applySession = useCallback((session: WorkoutSession | null) => {
    const previous = activeRef.current;
    const previousElapsed = previous?.client_session_id === session?.client_session_id
      ? currentElapsed()
      : 0;

    activeRef.current = session;
    anchorElapsedRef.current = session
      ? Math.max(session.elapsed_seconds || 0, previousElapsed)
      : 0;
    anchorTimeRef.current = Date.now();
    setActive(session);
    setElapsed(session ? anchorElapsedRef.current : 0);
    if (!session) setSelectedRpe(null);
  }, [currentElapsed]);

  const request = useCallback(async <T,>(path: string, options: RequestInit = {}) => {
    try {
      return await apiRequest<T>(path, options, token);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onUnauthorized();
      throw caught;
    }
  }, [onUnauthorized, token]);

  const patchActive = useCallback(async (
    patch: Record<string, unknown>,
    useRevision = true,
  ) => {
    const session = activeRef.current;
    if (!session) return null;
    const body = { ...patch };
    if (useRevision) body.expected_revision = session.revision;

    try {
      const updated = await request<WorkoutSession>(
        `/workouts/sessions/${encodeURIComponent(session.client_session_id)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      );
      if (updated.status === "completed" || updated.status === "cancelled") {
        applySession(null);
        await onSessionsChanged();
      } else {
        applySession(updated);
      }
      return updated;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        const conflict = caught.detail as ConflictDetail | undefined;
        if (conflict?.active_session) {
          applySession(conflict.active_session);
          return conflict.active_session;
        }
      }
      onError(caught instanceof Error ? caught.message : "Unable to update workout.");
      return null;
    }
  }, [applySession, onError, onSessionsChanged, request]);

  const start = useCallback(async (day: PlanDay) => {
    if (activeRef.current || day.type === "Rest") return null;
    try {
      const session = await request<WorkoutSession>("/workouts/sessions", {
        method: "POST",
        body: JSON.stringify({
          client_session_id: uuid(),
          source: "web",
          plan_date: day.date,
          workout_type: day.type || "Workout",
          title: day.title || day.type || "Workout",
          intensity: day.intensity || "",
          planned_duration_minutes: day.duration_minutes || 0,
          elapsed_seconds: 0,
        }),
      });
      applySession(session);
      return session;
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        const conflict = caught.detail as ConflictDetail | undefined;
        if (conflict?.active_session) {
          applySession(conflict.active_session);
          return conflict.active_session;
        }
      }
      onError(caught instanceof Error ? caught.message : "Unable to start workout.");
      return null;
    }
  }, [applySession, onError, request]);

  const pause = useCallback(() => patchActive({ status: "paused", elapsed_seconds: currentElapsed() }), [currentElapsed, patchActive]);
  const resume = useCallback(() => patchActive({ status: "running", elapsed_seconds: currentElapsed() }), [currentElapsed, patchActive]);
  const finish = useCallback(() => patchActive({
    status: "completed",
    elapsed_seconds: currentElapsed(),
    ...(selectedRpe ? { rpe: selectedRpe } : {}),
  }), [currentElapsed, patchActive, selectedRpe]);
  const cancel = useCallback(() => patchActive({ status: "cancelled", elapsed_seconds: currentElapsed() }), [currentElapsed, patchActive]);

  useEffect(() => {
    if (!token) {
      streamAbortRef.current?.abort();
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      applySession(null);
      setConnected(false);
      return;
    }

    let disposed = false;
    void request<WorkoutSession | null>("/workouts/active")
      .then((session) => {
        if (!disposed) applySession(session);
      })
      .catch((caught) => {
        if (!disposed) onError(caught instanceof Error ? caught.message : "Unable to load active workout.");
      });

    const connect = async () => {
      streamAbortRef.current?.abort();
      const controller = new AbortController();
      streamAbortRef.current = controller;
      try {
        const response = await fetch(apiUrl("/workouts/stream"), {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
          signal: controller.signal,
        });
        if (response.status === 401) {
          onUnauthorized();
          return;
        }
        if (!response.ok || !response.body) throw new Error("Live workout stream unavailable.");
        setConnected(true);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!disposed) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";
          for (const event of events) {
            const data = event.split("\n").find((line) => line.startsWith("data: "))?.slice(6);
            if (data === undefined) continue;
            const previousHadSession = Boolean(activeRef.current);
            const next = JSON.parse(data) as WorkoutSession | null;
            applySession(next);
            if (previousHadSession && !next) void onSessionsChanged();
          }
        }
        if (!disposed) throw new Error("Live workout stream ended.");
      } catch (caught) {
        if (disposed || (caught instanceof DOMException && caught.name === "AbortError")) return;
        setConnected(false);
        reconnectTimerRef.current = window.setTimeout(() => void connect(), 2000);
      }
    };

    void connect();
    return () => {
      disposed = true;
      streamAbortRef.current?.abort();
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
    };
  }, [applySession, onError, onSessionsChanged, onUnauthorized, request, token]);

  useEffect(() => {
    if (!token) return;
    const timer = window.setInterval(() => {
      const session = activeRef.current;
      if (!session) return;
      const nextElapsed = currentElapsed();
      setElapsed(nextElapsed);

      if (
        session.status === "running"
        && Date.now() - lastProgressPushRef.current >= 5000
        && !progressInFlightRef.current
      ) {
        lastProgressPushRef.current = Date.now();
        progressInFlightRef.current = true;
        void patchActive({ elapsed_seconds: nextElapsed }, false)
          .finally(() => { progressInFlightRef.current = false; });
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [currentElapsed, patchActive, token]);

  return {
    active,
    elapsed,
    connected,
    selectedRpe,
    setSelectedRpe,
    start,
    pause,
    resume,
    finish,
    cancel,
  };
}
